export function generatePowerShellScript(components, formSettings = {}) {
    // Default form settings
    const defaultSettings = {
      title: 'Generated GUI',
      width: 800,
      height: 600,
      backgroundColor: 'White',
      resizable: true,
      maximizable: true,
      windowStartupLocation: 'CenterScreen',
    };
    
    const settings = { ...defaultSettings, ...formSettings };

    // Helper function to get the active tab of a TabControl
    const getActiveTab = (tabControlId) => {
      const tabControl = components.find(c => c.id === tabControlId);
      return tabControl ? (tabControl.properties.activeTab || 0) : 0;
    };

    // Filter components to only include those that should be visible
    const getVisibleComponents = () => {
      const visibleComponents = [];
      
      components.forEach(comp => {
        if (comp.type === 'TabControl') {
          // Always include TabControl components
          visibleComponents.push(comp);
        } else if (comp.parentTab) {
          // Check if this component belongs to a TabControl and if that tab is active
          const tabControl = components.find(c => c.id === comp.parentTab);
          if (tabControl && tabControl.type === 'TabControl') {
            const activeTabIndex = getActiveTab(comp.parentTab);
            const componentTabIndex = comp.tabIndex || 0;
            
            if (componentTabIndex === activeTabIndex) {
              visibleComponents.push(comp);
            }
          }
        } else if (comp.type === 'Form') {
          // Include Form components (they will be filtered out later in XAML generation)
          visibleComponents.push(comp);
        } else {
          // Components not associated with any tab are always visible
          visibleComponents.push(comp);
        }
      });
      
      return visibleComponents;
    };

    const visibleComponents = getVisibleComponents();

    // Helper: Map component type to XAML
    const renderXaml = (comp) => {
      const { type, name, properties } = comp;
      
      // Calculate positioning based on parent Form
      let positioning = '';
      if (comp.parentForm) {
        // This control is positioned relative to a Form
        const parentForm = components.find(c => c.id === comp.parentForm);
        if (parentForm) {
          // The control's x,y are already relative to the Form, so use them directly
          positioning = `Canvas.Left="${properties.x}" Canvas.Top="${properties.y}"`;
        } else {
          // Fallback to absolute positioning if parent Form not found
          positioning = `Canvas.Left="${properties.x}" Canvas.Top="${properties.y}"`;
        }
      } else {
        // No parent Form, use absolute positioning
        positioning = `Canvas.Left="${properties.x}" Canvas.Top="${properties.y}"`;
      }
      
      const baseProps = `Name="${name}" Width="${properties.width}" Height="${properties.height}" ${positioning}`;
  
      switch (type) {
        case 'Button':
          return `<Button ${baseProps} Content="${properties.text || 'Button'}" />`;
        case 'Label':
          return `<Label ${baseProps} Content="${properties.text || 'Label'}" />`;
        case 'TextBox':
          const placeholder = properties.placeholder ? ` Text="${properties.placeholder}"` : '';
          return `<TextBox ${baseProps}${placeholder} />`;
        case 'Checkbox':
          return `<CheckBox ${baseProps} Content="${properties.text || 'CheckBox'}" />`;
        case 'Dropdown':
          let options = '';
          if (properties.options) {
            const optionList = properties.options.split('\n').filter(opt => opt.trim());
            options = optionList.map(opt => `<ComboBoxItem Content="${opt.trim()}"/>`).join('');
          } else {
            options = '<ComboBoxItem Content="Option 1"/><ComboBoxItem Content="Option 2"/>';
          }
          return `<ComboBox ${baseProps}>${options}</ComboBox>`;
        case 'ListBox':
          let listItems = '';
          if (properties.options) {
            const itemList = properties.options.split('\n').filter(item => item.trim());
            listItems = itemList.map(item => `<ListBoxItem Content="${item.trim()}"/>`).join('');
          } else {
            listItems = '<ListBoxItem Content="Item 1"/><ListBoxItem Content="Item 2"/><ListBoxItem Content="Item 3"/>';
          }
          return `<ListBox ${baseProps}>${listItems}</ListBox>`;
        case 'GroupBox':
          return `<GroupBox ${baseProps} Header="${properties.text || 'Group'}"><Grid></Grid></GroupBox>`;
        case 'RadioButton':
          return `<RadioButton ${baseProps} Content="${properties.text || 'Radio'}" GroupName="RadioGroup" />`;
        case 'ProgressBar':
          const progressValue = properties.value || 50;
          return `<ProgressBar ${baseProps} Value="${progressValue}" Maximum="100" />`;
        case 'Slider':
          const sliderValue = properties.value || 50;
          return `<Slider ${baseProps} Value="${sliderValue}" Minimum="0" Maximum="100" />`;
        case 'DateTimePicker':
          return `<DatePicker ${baseProps} SelectedDate="{x:Static sys:DateTime.Today}" />`;
        case 'NumericUpDown':
          const numericValue = properties.value || 0;
          return `<TextBox ${baseProps} Text="${numericValue}" />`;
        case 'RichTextBox':
          return `<RichTextBox ${baseProps}><FlowDocument><Paragraph>Multi-line text editor...</Paragraph></FlowDocument></RichTextBox>`;
        case 'PictureBox':
          return `<Image ${baseProps} Source="pack://application:,,,/Images/default.png" Stretch="Uniform" />`;
        case 'TabControl':
          let tabItems = '';
          if (properties.tabNames) {
            const tabNames = properties.tabNames.split('\n').filter(name => name.trim());
            const tabContents = properties.tabContent ? properties.tabContent.split('|') : [];
            tabItems = tabNames.map((tabName, index) => {
              const content = tabContents[index] ? tabContents[index].trim() : '';
              return `<TabItem Header="${tabName.trim()}"><Grid><TextBlock Text="${content}" Margin="10"/></Grid></TabItem>`;
            }).join('');
          } else {
            tabItems = '<TabItem Header="Tab 1"><Grid></Grid></TabItem><TabItem Header="Tab 2"><Grid></Grid></TabItem>';
          }
          return `<TabControl ${baseProps}>${tabItems}</TabControl>`;
        case 'TreeView':
          return `<TreeView ${baseProps}><TreeViewItem Header="Root"><TreeViewItem Header="Child 1"/></TreeViewItem></TreeView>`;
        case 'ListView':
          let listViewColumns = '';
          if (properties.columns) {
            const columnList = properties.columns.split('\n').filter(col => col.trim());
            listViewColumns = columnList.map(col => `<GridViewColumn Header="${col.trim()}" DisplayMemberBinding="{Binding ${col.trim()}}"/>`).join('');
          } else {
            listViewColumns = '<GridViewColumn Header="Name" DisplayMemberBinding="{Binding Name}"/><GridViewColumn Header="Type" DisplayMemberBinding="{Binding Type}"/>';
          }
          const viewMode = properties.viewMode || 'Details';
          const multiSelect = properties.multiSelect !== false ? 'SelectionMode="Extended"' : '';
          return `<ListView ${baseProps} ${multiSelect} View="{StaticResource ${viewMode}View}"><ListView.View><GridView>${listViewColumns}</GridView></ListView.View></ListView>`;
        case 'ComboBox':
          let comboBoxOptions = '';
          if (properties.options) {
            const optionList = properties.options.split('\n').filter(opt => opt.trim());
            comboBoxOptions = optionList.map(opt => `<ComboBoxItem Content="${opt.trim()}"/>`).join('');
          } else {
            comboBoxOptions = '<ComboBoxItem Content="Option 1"/><ComboBoxItem Content="Option 2"/>';
          }
          const editable = properties.editable !== false ? 'IsEditable="True"' : '';
          const autoComplete = properties.autoComplete !== false ? 'IsTextSearchEnabled="True"' : '';
          return `<ComboBox ${baseProps} ${editable} ${autoComplete}>${comboBoxOptions}</ComboBox>`;
        case 'CheckedListBox':
          let checkedListBoxItems = '';
          if (properties.items) {
            const itemList = properties.items.split('\n').filter(item => item.trim());
            checkedListBoxItems = itemList.map(item => `<CheckBox Content="${item.trim()}" Margin="2"/>`).join('');
          } else {
            checkedListBoxItems = '<CheckBox Content="Item 1" Margin="2"/><CheckBox Content="Item 2" Margin="2"/>';
          }
          const threeState = properties.threeState !== false ? 'IsThreeState="True"' : '';
          return `<ScrollViewer ${baseProps}><StackPanel ${threeState}>${checkedListBoxItems}</StackPanel></ScrollViewer>`;
        case 'SplitContainer':
          const orientation = properties.orientation || 'Horizontal';
          const splitterDistance = properties.splitterDistance || 50;
          const fixedPanel = properties.fixedPanel !== false ? 'IsFixed="True"' : '';
          if (orientation === 'Horizontal') {
            return `<Grid ${baseProps}><Grid.RowDefinitions><RowDefinition Height="${splitterDistance}*"/><RowDefinition Height="5"/><RowDefinition Height="${100-splitterDistance}*"/></Grid.RowDefinitions><Border Grid.Row="0" Background="LightGray"><TextBlock Text="Panel 1" HorizontalAlignment="Center" VerticalAlignment="Center"/></Border><GridSplitter Grid.Row="1" HorizontalAlignment="Stretch" Background="Gray"/><Border Grid.Row="2" Background="LightBlue"><TextBlock Text="Panel 2" HorizontalAlignment="Center" VerticalAlignment="Center"/></Border></Grid>`;
          } else {
            return `<Grid ${baseProps}><Grid.ColumnDefinitions><ColumnDefinition Width="${splitterDistance}*"/><ColumnDefinition Width="5"/><ColumnDefinition Width="${100-splitterDistance}*"/></Grid.ColumnDefinitions><Border Grid.Column="0" Background="LightGray"><TextBlock Text="Panel 1" HorizontalAlignment="Center" VerticalAlignment="Center"/></Border><GridSplitter Grid.Column="1" VerticalAlignment="Stretch" Background="Gray"/><Border Grid.Column="2" Background="LightBlue"><TextBlock Text="Panel 2" HorizontalAlignment="Center" VerticalAlignment="Center"/></Border></Grid>`;
          }
        case 'CommandOutput':
          const defaultCommand = properties.defaultCommand || 'Get-Process';
          const outputFormat = properties.outputFormat || 'Table';
          const autoExecute = properties.autoExecute !== false ? 'true' : 'false';
          const showErrors = properties.showErrors !== false ? 'true' : 'false';
          return `<TextBox ${baseProps} IsReadOnly="True" FontFamily="Consolas" Background="Black" Foreground="White" VerticalScrollBarVisibility="Auto" Text="{Binding CommandOutput}"/>`;
        case 'ParameterPanel':
          const commandName = properties.commandName || 'Get-Process';
          const showHelp = properties.showHelp !== false ? 'true' : 'false';
          const validateInput = properties.validateInput !== false ? 'true' : 'false';
          return `<StackPanel ${baseProps}><TextBlock Text="${commandName} Parameters" FontWeight="Bold" Margin="5"/><StackPanel Orientation="Horizontal" Margin="5"><TextBlock Text="Name:" VerticalAlignment="Center" Margin="0,0,5,0"/><TextBox Width="150" Margin="0,0,10,0"/></StackPanel><StackPanel Orientation="Horizontal" Margin="5"><TextBlock Text="Id:" VerticalAlignment="Center" Margin="0,0,5,0"/><TextBox Width="150" Margin="0,0,10,0"/></StackPanel><CheckBox Content="IncludeUsername" Margin="5"/></StackPanel>`;
        case 'DataGrid':
          return `<DataGrid ${baseProps} AutoGenerateColumns="False">
            <DataGrid.Columns>
              <DataGridTextColumn Header="Name" Binding="{Binding Name}" Width="*"/>
              <DataGridTextColumn Header="Type" Binding="{Binding Type}" Width="*"/>
              <DataGridTextColumn Header="Size" Binding="{Binding Size}" Width="*"/>
              <DataGridTextColumn Header="Status" Binding="{Binding Status}" Width="*"/>
            </DataGrid.Columns>
          </DataGrid>`;
        case 'MenuStrip':
          return `<Menu ${baseProps}><MenuItem Header="File"/><MenuItem Header="Edit"/><MenuItem Header="Help"/></Menu>`;
        case 'ToolStrip':
          return `<ToolBar ${baseProps}><Button Content="A"/><Button Content="B"/><Button Content="C"/></ToolBar>`;
        case 'StatusStrip':
          return `<StatusBar ${baseProps}><StatusBarItem Content="Ready"/></StatusBar>`;
        case 'Panel':
          return `<Border ${baseProps} Background="LightGray" BorderBrush="Gray" BorderThickness="1"><Grid></Grid></Border>`;
        case 'Separator':
          return `<Separator ${baseProps} Background="Gray" />`;
        case 'LinkLabel':
          return `<Hyperlink ${baseProps} NavigateUri="http://example.com" TargetName="_blank">${properties.text || 'Click here'}</Hyperlink>`;
        case 'MonthCalendar':
          return `<Calendar ${baseProps} DisplayMode="Month" />`;
        case 'ColorDialog':
          return `<Button ${baseProps} Content="Color Picker" Background="LightBlue" />`;
        case 'OpenFileDialog':
          return `<Button ${baseProps} Content="Open File" Background="LightGreen" />`;
        case 'SaveFileDialog':
          return `<Button ${baseProps} Content="Save File" Background="Orange" />`;
        case 'FolderBrowserDialog':
          return `<Button ${baseProps} Content="Browse Folder" Background="Purple" Foreground="White" />`;
        case 'PrintDialog':
          return `<Button ${baseProps} Content="Print" Background="Gray" Foreground="White" />`;
        case 'FontDialog':
          return `<Button ${baseProps} Content="Font" Background="Indigo" Foreground="White" />`;
        case 'Timer':
          return `<TextBlock ${baseProps} Text="Timer Component" Background="Yellow" Padding="5" />`;
        case 'NotifyIcon':
          return `<TextBlock ${baseProps} Text="Notify Icon Component" Background="DarkGray" Foreground="White" Padding="5" />`;
        default:
          return '';
      }
    };

    // Calculate window size based on component positions or use form settings
    const forms = components.filter(c => c.type === 'Form');
    let windowWidth, windowHeight;
    
    // Compensation for window title bar and borders
    const titleBarHeight = 30; // Approximate height of title bar
    const borderWidth = 8; // Approximate width of window borders
    
    if (forms.length > 0) {
      // Use the first form's size for the window
      const mainForm = forms[0];
      windowWidth = mainForm.properties.width + (borderWidth * 2); // Add border compensation
      windowHeight = mainForm.properties.height + titleBarHeight + (borderWidth * 2); // Add title bar and border compensation
    } else {
      // Calculate window size based on component positions
      const maxX = Math.max(...components.map(c => c.properties.x + c.properties.width), settings.width - 50);
      const maxY = Math.max(...components.map(c => c.properties.y + c.properties.height), settings.height - 50);
      windowWidth = Math.max(maxX + 50, settings.width) + (borderWidth * 2);
      windowHeight = Math.max(maxY + 50, settings.height) + titleBarHeight + (borderWidth * 2);
    }

    // Check if there are Form components and use their properties
    let windowTitle = settings.title;
    let windowStartupLocation = settings.windowStartupLocation || 'CenterScreen';
    
    if (forms.length > 0) {
      // Use the first form's properties for the main window
      const mainForm = forms[0];
      windowTitle = mainForm.properties.formTitle || mainForm.name || settings.title;
      windowStartupLocation = mainForm.properties.windowStartupLocation || 'CenterScreen';
    }

    // Build window properties
    const windowProps = [
      `Title="${windowTitle}"`,
      `Height="${windowHeight}"`,
      `Width="${windowWidth}"`,
      `WindowStartupLocation="${windowStartupLocation}"`,
      settings.resizable ? '' : `ResizeMode="NoResize"`,
      settings.maximizable ? '' : `WindowStyle="SingleBorderWindow"`,
    ].filter(prop => prop !== '').join(' ');
  
    // Generate XAML - exclude Form components from rendering
    const xamlControls = getVisibleComponents()
      .filter(comp => comp.type !== 'Form') // Exclude Form components from XAML generation
      .map(renderXaml)
      .join('\n        ');
  
    const xaml = `
  <Window xmlns="http://schemas.microsoft.com/winfx/2006/xaml/presentation"
          xmlns:x="http://schemas.microsoft.com/winfx/2006/xaml"
          xmlns:sys="clr-namespace:System;assembly=mscorlib"
          ${windowProps}>
    <Canvas Background="${settings.backgroundColor}">
          ${xamlControls}
    </Canvas>
  </Window>`.trim();
  
    // Generate PowerShell
    const powershell = `
# Generated PowerShell GUI Script
# Created with PowerShell GUI Builder
# Note: Window dimensions include compensation for title bar and borders

Add-Type -AssemblyName PresentationFramework

[xml]$xaml = @"
${xaml}
"@

try {
    $reader = (New-Object System.Xml.XmlNodeReader $xaml)
    $window = [Windows.Markup.XamlReader]::Load($reader)

    # Find controls and bind events
    ${visibleComponents.filter(c => c.type !== 'Form').map(c => `$${c.name} = $window.FindName("${c.name}")`).join('\n    ')}

    # Add sample data for DataGrid components
    ${visibleComponents.filter(c => c.type === 'DataGrid').map(c => `
    # Sample data for ${c.name}
    $${c.name}Data = @(
        [PSCustomObject]@{ Name = "Document1.txt"; Type = "Text"; Size = "2.5 KB"; Status = "Active" },
        [PSCustomObject]@{ Name = "Image.jpg"; Type = "Image"; Size = "1.2 MB"; Status = "Ready" },
        [PSCustomObject]@{ Name = "Data.csv"; Type = "CSV"; Size = "45 KB"; Status = "Processing" },
        [PSCustomObject]@{ Name = "Report.pdf"; Type = "PDF"; Size = "3.1 MB"; Status = "Complete" }
    )
    $${c.name}.ItemsSource = $${c.name}Data`).join('\n    ')}

    # Add event handlers here
    # Example: $button1.Add_Click({ Write-Host "Button clicked!" })

    # Add dialog event handlers
    ${visibleComponents.filter(c => ['ColorDialog', 'OpenFileDialog', 'SaveFileDialog', 'FolderBrowserDialog', 'PrintDialog', 'FontDialog'].includes(c.type)).map(c => `
    # Event handler for ${c.name}
    $${c.name}.Add_Click({
        switch ("${c.type}") {
            "ColorDialog" {
                $colorDialog = New-Object System.Windows.Forms.ColorDialog
                if ($colorDialog.ShowDialog() -eq [System.Windows.Forms.DialogResult]::OK) {
                    Write-Host "Selected color: $($colorDialog.Color)"
                }
            }
            "OpenFileDialog" {
                $openFileDialog = New-Object Microsoft.Win32.OpenFileDialog
                $openFileDialog.Filter = "All files (*.*)|*.*"
                if ($openFileDialog.ShowDialog() -eq $true) {
                    Write-Host "Selected file: $($openFileDialog.FileName)"
                }
            }
            "SaveFileDialog" {
                $saveFileDialog = New-Object Microsoft.Win32.SaveFileDialog
                $saveFileDialog.Filter = "Text files (*.txt)|*.txt|All files (*.*)|*.*"
                if ($saveFileDialog.ShowDialog() -eq $true) {
                    Write-Host "Save to file: $($saveFileDialog.FileName)"
                }
            }
            "FolderBrowserDialog" {
                $folderDialog = New-Object System.Windows.Forms.FolderBrowserDialog
                if ($folderDialog.ShowDialog() -eq [System.Windows.Forms.DialogResult]::OK) {
                    Write-Host "Selected folder: $($folderDialog.SelectedPath)"
                }
            }
            "PrintDialog" {
                $printDialog = New-Object System.Windows.Forms.PrintDialog
                if ($printDialog.ShowDialog() -eq [System.Windows.Forms.DialogResult]::OK) {
                    Write-Host "Print dialog confirmed"
                }
            }
            "FontDialog" {
                $fontDialog = New-Object System.Windows.Forms.FontDialog
                if ($fontDialog.ShowDialog() -eq [System.Windows.Forms.DialogResult]::OK) {
                    Write-Host "Selected font: $($fontDialog.Font)"
                }
            }
        }
    })`).join('\n    ')}

    # Show the window
    $window.ShowDialog()
} catch {
    Write-Error "Error loading GUI: $_"
    Read-Host "Press Enter to exit"
}`.trim();
  
    return powershell;
  }
  